/**
 * LatticeCore — the orb rebuilt as structure instead of a swarm.
 *
 * The original diagnosis was that the shipped orb's density IS its design:
 * ~1,400 points of turbulence with no silhouette, no interior and no edge.
 * This keeps the density but makes it BUILD A FORM — a subdivided icosahedron
 * whose struts are the connections and whose vertices are the nodes.
 *
 * Colour comes from `getOrbPalette(theme)`, not from literals, so light mode
 * inherits the existing material model (absorptive pigment + warm highlights on
 * cream) rather than being an inverted dark mode.
 *
 * STATE LIVES HERE, NEVER IN THE RING — the operator's ruling. The zodiac ring
 * is a calendar; every state signal is expressed by this component:
 *   thinking  -> faster spin, node pulse quickens, struts brighten
 *   speaking  -> amplitude scale pulse
 *   listening -> the lattice SETTLES: spin slows, cage expands, nodes steady.
 *                That is the shared grammar from the sweep - the instrument
 *                resolving when it is paying attention.
 *   error     -> 3s window: still, contracted to 0.92, nodes + struts tinted
 *                palette.errorTint, then back to idle (auto-return).
 *
 * The shipped hero core since 2026-09-02 (spec docs/superpowers/specs/2026-09-02-orb-reface-integration-design.md).
 */
import React, { useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame, invalidate } from '@react-three/fiber';
import * as THREE from 'three';
import { getOrbPalette } from './orbPalette.js';
import { wheelHue } from './zodiacRing.js';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion.js';

const DETAIL = 2;        // icosahedron subdivision: 42 vertices, 120 edges
const RADIUS = 1.32;

// Error window length. Design-system orb-state rule: "--status-error core,
// contract, auto-return to idle ~3s". Opens on the RISING edge of isError and
// closes on its own even if the prop stays true.
const ERROR_WINDOW_MS = 3000;

/**
 * Round point sprite. THREE's default PointsMaterial renders each point as a
 * SQUARE, which at node size reads as cheap pixel dice rather than light. A
 * radial-gradient texture gives them a soft circular falloff.
 */
function nodeSprite() {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0.00, 'rgba(255,255,255,1)');
  grad.addColorStop(0.28, 'rgba(255,255,255,0.85)');
  grad.addColorStop(0.62, 'rgba(255,255,255,0.22)');
  grad.addColorStop(1.00, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return tex;
}

/** HSL -> linear RGB triple, for writing straight into a colour attribute. */
function hsl(h, sat, light) {
  const c = new THREE.Color();
  c.setHSL(((h % 360) + 360) % 360 / 360, sat, light);
  return [c.r, c.g, c.b];
}

/**
 * Light-mode tint: the lattice joins the ring's colour wheel instead of sitting
 * in flat burnt-orange. Hue comes from the vertex's AZIMUTH, using the ring's
 * own `wheelHue` so both are literally on the same wheel rather than two
 * near-matching ones. Values stay pigment-dark (not pastel) because on cream the
 * core has to hold contrast against the pale sector bands around it.
 */
function spectralTint(v, forStrut = false) {
  const azimuth = Math.atan2(v.x, v.z) * 180 / Math.PI;
  const lift = 0.05 * (v.y / 1.32);            // slight polar shading
  // Struts run DARKER than nodes on purpose. WebGL caps LineBasicMaterial at a
  // 1px hairline, and a 1px antialiased line at mid-lightness dissolves into
  // cream - dark mode hid this because additive blending made the same lines
  // glow. Nodes are sprites with real area, so they can sit lighter.
  return forStrut
    ? hsl(wheelHue(azimuth), 0.72, 0.17 + lift * 0.5)
    : hsl(wheelHue(azimuth), 0.68, 0.27 + lift);
}

/** Unique vertices + unique edges from a subdivided icosahedron. */
function buildLattice(radius, detail) {
  const geo = new THREE.IcosahedronGeometry(radius, detail);
  const pos = geo.attributes.position;

  // IcosahedronGeometry is non-indexed and duplicates shared vertices, so
  // dedupe by quantised position or every node renders 5-6 times over.
  const key = (i) => [pos.getX(i), pos.getY(i), pos.getZ(i)]
    .map((v) => v.toFixed(4)).join(',');
  const index = new Map();
  const verts = [];
  const idOf = [];
  for (let i = 0; i < pos.count; i++) {
    const k = key(i);
    if (!index.has(k)) {
      index.set(k, verts.length);
      verts.push(new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)));
    }
    idOf.push(index.get(k));
  }

  const seen = new Set();
  const edges = [];
  for (let t = 0; t < idOf.length; t += 3) {
    const tri = [idOf[t], idOf[t + 1], idOf[t + 2]];
    for (let e = 0; e < 3; e++) {
      const a = tri[e]; const b = tri[(e + 1) % 3];
      const k = a < b ? `${a}_${b}` : `${b}_${a}`;
      if (!seen.has(k)) { seen.add(k); edges.push([a, b]); }
    }
  }
  geo.dispose();
  return { verts, edges };
}

function Lattice({ isThinking, isSpeaking, isListening, errorUntil, reduced, palette, spectral }) {
  const group = useRef();
  const nodeMat = useRef();
  const strutMat = useRef();
  const nodeGeo = useRef();

  const { verts, edges } = useMemo(() => buildLattice(RADIUS, DETAIL), []);
  const sprite = useMemo(() => nodeSprite(), []);
  const errorColor = useMemo(() => new THREE.Color(palette.errorTint), [palette]);
  const restColor  = useMemo(() => new THREE.Color(0xffffff), []);

  // Struts: one line segment per unique edge.
  const vertexColour = useMemo(() => {
    const bands = [palette.particleFire, palette.particleGold, palette.particleWarmWhite];
    return (v, i, forStrut = false) => {
      if (spectral) return spectralTint(v, forStrut);
      const b = bands[i % bands.length];
      const lerp = (mn, mx) => mn + (mx - mn) * ((i * 0.618) % 1);
      return [lerp(b.rMin, b.rMax), lerp(b.gMin, b.gMax), lerp(b.bMin, b.bMax)];
    };
  }, [palette, spectral]);

  const strutGeo = useMemo(() => {
    const arr = new Float32Array(edges.length * 6);
    const col = new Float32Array(edges.length * 6);
    edges.forEach(([a, b], i) => {
      arr.set([verts[a].x, verts[a].y, verts[a].z,
        verts[b].x, verts[b].y, verts[b].z], i * 6);
      // each endpoint takes its own vertex colour, so the strut GRADIENTS
      // between hues and the wheel reads along the edges, not just at the nodes
      col.set([...vertexColour(verts[a], a, true), ...vertexColour(verts[b], b, true)], i * 6);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    return g;
  }, [verts, edges, vertexColour]);

  // Nodes: one point per unique vertex, tinted along the palette's warm bands
  // so the lattice keeps the organic colour variation the swarm had.
  const nodes = useMemo(() => {
    const p = new Float32Array(verts.length * 3);
    const c = new Float32Array(verts.length * 3);
    verts.forEach((v, i) => {
      p.set([v.x, v.y, v.z], i * 3);
      c.set(vertexColour(v, i), i * 3);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(p, 3));
    g.setAttribute('color', new THREE.BufferAttribute(c, 3));
    return g;
  }, [verts, vertexColour]);

  useFrame(({ clock }, delta) => {
    const t = clock.getElapsedTime();
    if (!group.current) return;

    if (reduced) {
      // Still image at the idle pose. State changes still re-render once
      // (frameloop="demand"), so the five states stay distinguishable:
      // error tints, listening opens the cage — nothing moves on its own.
      const inError = performance.now() < errorUntil.current;
      group.current.rotation.set(0.14, 0.6, 0);
      group.current.scale.setScalar(inError ? 0.92 : isListening ? 1.06 : 1);
      if (nodeMat.current) {
        nodeMat.current.color.copy(inError ? errorColor : restColor);
        nodeMat.current.opacity = 0.85;
        nodeMat.current.size = 0.17 * (spectral ? 1.25 : 1);
      }
      if (strutMat.current) {
        if (inError) strutMat.current.color.copy(errorColor);
        else strutMat.current.color.set(spectral ? 0xffffff : palette.connectionColor);
        strutMat.current.opacity = Math.min(0.95, palette.connectionAlphaIdle * (spectral ? 3.6 : 2.4) * 1.6);
      }
      return;
    }

    // Precedence: error window > listening > thinking > speaking > idle.
    // Error is an interruption; it wins for its 3s and then hands back.
    if (performance.now() < errorUntil.current) {
      group.current.scale.setScalar(0.92);
      if (nodeMat.current) {
        nodeMat.current.color.copy(errorColor);
        nodeMat.current.opacity = 0.9;
      }
      if (strutMat.current) {
        strutMat.current.color.copy(errorColor);
        strutMat.current.opacity = Math.min(0.95, palette.connectionAlphaIdle * (spectral ? 3.6 : 2.4) * 1.2);
      }
      return;
    }
    if (nodeMat.current) nodeMat.current.color.copy(restColor);
    if (strutMat.current) strutMat.current.color.set(spectral ? 0xffffff : palette.connectionColor);

    // listening = settle. Slow the spin and let the cage open slightly.
    // delta, not a fixed 0.016: per-frame advance runs 2x fast on a 120Hz
    // display, and a review harness must not animate differently per monitor.
    const spin = isListening ? 0.035 : isThinking ? 0.20 : 0.075;
    group.current.rotation.y += spin * delta;
    group.current.rotation.x = Math.sin(t * 0.13) * 0.14;

    const breathe = isSpeaking ? 1 + Math.sin(t * 7.3) * 0.035
      : isThinking ? 1 + Math.sin(t * 4.1) * 0.018
        : 1 + Math.sin(t * 1.15) * 0.010;
    const open = isListening ? 1.06 : 1;
    const s = breathe * open;
    group.current.scale.set(s, s, s);

    if (nodeMat.current) {
      // nodes steady when listening, quicken when thinking
      // The first pass read as a faint wireframe: the shipped orb gets its glow
      // from 1,400 overlapping additive points, and 42 nodes cannot fake that at
      // the same per-point values. Nodes carry the light here, so they run
      // larger and hotter.
      const pulse = isListening ? 0.98
        : isThinking ? 0.86 + Math.sin(t * 5.2) * 0.14
          : 0.80 + Math.sin(t * 1.7) * 0.14;
      nodeMat.current.opacity = pulse;
      nodeMat.current.size = (isListening ? 0.20 : 0.17)
        * (isSpeaking ? 1.14 : 1) * (spectral ? 1.25 : 1);
    }
    if (strutMat.current) {
      // x2.4 on the palette's connection alphas: those values were tuned for a
      // cloud where many lines overlap, and a lattice draws each edge exactly once.
      // cream needs more strut than black does - pigment on a light ground has
      // less contrast to work with than additive light on a dark one
      const G = spectral ? 3.6 : 2.4;
      // Clamp below 1: at G=3.6 the light-mode products all saturate past 1.0,
      // the GPU clamps them flat, and the state pulses become invisible.
      strutMat.current.opacity = Math.min(0.95, isThinking
        ? (palette.connectionAlphaThinkBase + Math.sin(t * 4.6) * palette.connectionAlphaThinkAmp) * G
        : isSpeaking
          ? (palette.connectionAlphaSpeakBase + Math.sin(t * 7.1) * palette.connectionAlphaSpeakAmp) * G
          : isListening ? palette.connectionAlphaIdle * G * 2.2 : palette.connectionAlphaIdle * G * 1.6);
    }
  });

  return (
    <group ref={group}>
      <lineSegments geometry={strutGeo}>
        <lineBasicMaterial
          ref={strutMat}
          {...(spectral
            ? { vertexColors: true }
            : { color: palette.connectionColor })}
          transparent
          opacity={palette.connectionAlphaIdle * 3.8}
        />
      </lineSegments>
      <points geometry={nodes} ref={nodeGeo}>
        <pointsMaterial
          ref={nodeMat}
          size={0.17}
          map={sprite}
          alphaTest={0.01}
          vertexColors
          transparent
          opacity={0.85}
          sizeAttenuation
          depthWrite={false}
          // Additive blending is right on black but blows coloured pigment out to
          // white on cream, which would destroy the wheel. Normal blending in light.
          blending={spectral ? THREE.NormalBlending : THREE.AdditiveBlending}
        />
      </points>
    </group>
  );
}

export default function LatticeCore({
  isThinking = false,
  isSpeaking = false,
  isListening = false,
  isError = false,
  theme = 'dark',
  onOrbClick,
  size = 250,
}) {
  const palette = useMemo(() => getOrbPalette(theme), [theme]);
  const reduced = usePrefersReducedMotion();

  // Rising-edge trigger. A prop that stays true does not re-open the window.
  const errorUntil = useRef(0);
  useEffect(() => {
    if (isError) errorUntil.current = performance.now() + ERROR_WINDOW_MS;
  }, [isError]);

  // Under reduced motion the Canvas renders on demand, and every state lives in
  // refs read inside useFrame — nothing on a three.js element changes, so r3f
  // never invalidates on its own. Ask for one frame per state change, and one
  // more just after the error window closes so the auto-return still repaints.
  // (Roster review 2026-09-02: without this the orb froze at its first paint for
  // reduced-motion users — the stillness proof never clicked a state.)
  useEffect(() => {
    if (!reduced) return undefined;
    // Two frames, not one: on r3f 8.18's demand loop the first frame after a
    // prop change consistently presented the PREVIOUS pose (verified with a
    // pixel diff across idle→listening→speaking→listening→thinking, 2026-09-02);
    // a second frame 40 ms later shows the new one.
    invalidate();
    const t1 = setTimeout(invalidate, 40);
    const t2 = isError ? setTimeout(invalidate, ERROR_WINDOW_MS + 50) : 0;
    return () => { clearTimeout(t1); if (t2) clearTimeout(t2); };
  }, [reduced, isThinking, isSpeaking, isListening, isError]);
  return (
    <div
      className="lattice-core"
      style={{ width: size, height: size }}
      onClick={onOrbClick}
      role="presentation"
    >
      {/* z=3.2 with fov 50 frames a radius-1.32 lattice at ~85% of the viewport.
          The shipped orb's 4.2 leaves a structured form looking tiny - the
          particle cloud got away with it because it spread far wider. */}
      <Canvas
        frameloop={reduced ? 'demand' : 'always'}
        camera={{ position: [0, 0, 3.2], fov: 50 }}
        gl={{ antialias: true, alpha: true }}
      >
        <ambientLight intensity={theme === 'light' ? 0.9 : 0.55} />
        <pointLight position={[2.4, 2.0, 3.2]} intensity={1.5} color={palette.pointLightPrimary} />
        <pointLight position={[-2.6, -1.6, 2.0]} intensity={0.9} color={palette.pointLightSecondary} />
        <Lattice
          isThinking={isThinking}
          isSpeaking={isSpeaking}
          isListening={isListening}
          errorUntil={errorUntil}
          reduced={reduced}
          palette={palette}
          spectral={theme === 'light'}
        />
      </Canvas>
    </div>
  );
}
